import { useEffect, useState } from 'react'
import Header from './containers/Header'
import { Provider } from 'react-redux'
import { ReduxStore } from './redux/config'
import events, { Event } from './pubsub/events'
import * as coreAPIEvents from 'utils/sendCoreAPIEvent'
import trackWithTealium from 'utils/trackWithTealium'
import eventsV2 from 'pubsub/eventsV2'
import { coreEventSubscriber } from 'pubsub/core/coreEventSubscriber'
import { tealiumEventSubscriber } from 'pubsub/tealium/tealiumEventSubscriber'
import i18n from 'i18n/config'
import { LanguagePreference } from 'models/LanguagePreference'
import { BrowserApi } from 'components/BrowserApi/BrowserApi'

export interface AppProps {
  currentLangProps: LanguagePreference
  activeNavSection?: string;
  languageToggleLink: string;
  loginUrl: string;
  logoutUrl: string;
  enableLiveChat: boolean;
  enableMessageCenter: boolean;
  disableLogin: boolean;
  liveChatViewpage?: string;
  enableTimeout?: boolean;
  store: ReduxStore
}

const App: React.FC<AppProps> = (
  {
    currentLangProps,
    activeNavSection,
    languageToggleLink,
    loginUrl,
    logoutUrl,
    enableLiveChat,
    disableLogin,
    enableMessageCenter,
    liveChatViewpage,
    enableTimeout,
    store
  }) => {
  // core page load analytics
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (loading === false) {
      const pageLoadEvent: Event = {
        authenticated: false,
        text: '',
        category: '',
        activity: 'Page Load',
        url: window.location.href,
        language: i18n.language,
        title: document.title,
        sendToTealium: false,
        sendToCoreAPI: true,
        isMessageCenter: false
      }
      events.publish(pageLoadEvent)
    }
  }, [loading])
  // pubsub for analytics
  useEffect(() => {
    // v1
    coreAPIEvents.startListening()
    trackWithTealium.startListening()
    // v2
    const coreSubscriber = coreEventSubscriber.subscribe(eventsV2)
    const tealiumSubscriber = tealiumEventSubscriber.subscribe(eventsV2)

    setLoading(false)
    return () => {
      // v1
      coreAPIEvents.unsubscribe()
      trackWithTealium.unsubscribe()
      // v2
      coreSubscriber.unsubscribe()
      tealiumSubscriber.unsubscribe()
    }
  }, [])

  const loadAndDisplayToasts = Boolean(
    process.env.REACT_APP_DISPLAY_TOASTS_URL_PATH &&
    window.location.pathname.startsWith(process.env.REACT_APP_DISPLAY_TOASTS_URL_PATH)
  )

  return (
    <Provider store={store}>
      <BrowserApi
        timeoutEnabled={enableTimeout}
        displayToasts={loadAndDisplayToasts}
      />
      <Header
        currentLangProps={currentLangProps}
        activeNavSection={activeNavSection}
        disableLogin={disableLogin}
        languageToggleLink={languageToggleLink}
        loginUrl={loginUrl}
        logoutUrl={logoutUrl}
        enableLiveChat={enableLiveChat}
        enableMessageCenter={enableMessageCenter}
        liveChatViewpage={liveChatViewpage}
        loadAndDisplayToasts={loadAndDisplayToasts}
      />
    </Provider>)
}

export default App
